import { CountryDistance, CountryData, parseCSVLine, OptimizedCountryPair, parseOptimizedDistanceData, findOptimizedDistanceBetweenCountries, Centroid } from './utils'

export class DataService {
  private static instance: DataService
  private countryDistances: CountryDistance[] = []
  private optimizedPairs: OptimizedCountryPair[] = []
  private countryData: CountryData | null = null
  private centroids: Centroid[] = []
  private enclavePairs: [string, string][] = [] // [inner, outer]
  private isLoaded = false
  private useOptimizedFormat = true

  static getInstance(): DataService {
    if (!DataService.instance) {
      DataService.instance = new DataService()
    }
    return DataService.instance
  }

  async loadData(): Promise<void> {
    if (this.isLoaded) {
      console.log('DataService: Data already loaded, skipping')
      return
    }

    console.log('DataService: Starting data load...')
    try {
      // Try to load optimized JSON format first
      try {
        console.log('DataService: Attempting to fetch optimized JSON data...')
        const jsonResponse = await fetch('/data/country-distances-directions.json')
        if (jsonResponse.ok) {
          const jsonData = await jsonResponse.json()
          this.optimizedPairs = parseOptimizedDistanceData(jsonData)
          console.log(`DataService: Loaded ${this.optimizedPairs.length} optimized country pairs`)
          
          // Log first few entries for verification
          if (this.optimizedPairs.length > 0) {
            console.log('DataService: Sample optimized entries:', 
              this.optimizedPairs.slice(0, 3).map(p => ({
                countries: p.countries,
                distance: p.distance
              }))
            )
          }
        } else {
          throw new Error('Optimized JSON not available')
        }
      } catch (optimizedError) {
        console.log('DataService: Optimized format not available, falling back to CSV...')
        this.useOptimizedFormat = false
        
        // Fallback to CSV data
        console.log('DataService: Fetching CSV data...')
        const csvResponse = await fetch('/data/country-distances-directions.csv')
        const csvText = await csvResponse.text()
        const lines = csvText.split('\n')
        console.log(`DataService: CSV loaded with ${lines.length} lines`)
        
        let parsedCount = 0
        let skippedCount = 0
        
        // Skip header and parse each line
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim()
          if (line) {
            const parsed = parseCSVLine(line)
            if (parsed) {
              this.countryDistances.push(parsed)
              parsedCount++
              
              // Log first few entries for verification
              if (parsedCount <= 3) {
                console.log(`DataService: Sample entry ${parsedCount}:`, {
                  fromCountry: parsed.fromCountry,
                  toCountry: parsed.toCountry,
                  distance: parsed.distanceKm
                })
              }
            } else {
              skippedCount++
            }
          }
        }
        
        console.log(`DataService: Parsed ${parsedCount} distance entries, skipped ${skippedCount} invalid lines`)
      }

      // Load JSON data - prefer split data with regions if available
      console.log('DataService: Fetching country data JSON...')
      let jsonResponse = await fetch('/data/country_data_with_regions.json')
      if (!jsonResponse.ok) {
        console.log('DataService: No split country data found, falling back to original')
        jsonResponse = await fetch('/data/country_data.json')
      }
      if (!jsonResponse.ok) {
        throw new Error(`Failed to fetch country data: ${jsonResponse.status} ${jsonResponse.statusText}`)
      }
      this.countryData = await jsonResponse.json()
      console.log(`DataService: Country data loaded with ${this.countryData?.features?.length || 0} features`)

      // Load centroids
      console.log('DataService: Fetching centroids...')
      const centersResponse = await fetch('/data/centroids.json')
      if (!centersResponse.ok) {
        throw new Error(`Failed to fetch centroids: ${centersResponse.status} ${centersResponse.statusText}`)
      }
      const centersData = await centersResponse.json()
      
      // Parse centroids from GeoJSON format
      this.centroids = centersData.features.map((feature: any) => ({
        country: feature.properties.COUNTRY,
        latitude: feature.geometry.coordinates[1],
        longitude: feature.geometry.coordinates[0]
      }))
      console.log(`DataService: Loaded ${this.centroids.length} centroids`)

      // Load enclaves sidecar (optional — falls back to empty list)
      try {
        const enclavesResponse = await fetch('/data/enclaves.json')
        if (enclavesResponse.ok) {
          const enclavesData = await enclavesResponse.json()
          this.enclavePairs = (enclavesData?.pairs ?? []) as [string, string][]
          console.log(`DataService: Loaded ${this.enclavePairs.length} enclave pair(s)`)
        }
      } catch {
        this.enclavePairs = []
      }

      this.isLoaded = true
      console.log('DataService: Data loading completed successfully')
    } catch (error) {
      console.error('DataService: Failed to load data:', error)
      throw error
    }
  }

  getCountryDistances(): CountryDistance[] {
    return this.countryDistances
  }

  getCountryData(): CountryData | null {
    return this.countryData
  }

  getCentroids(): Centroid[] {
    return this.centroids
  }

  /**
   * Look up whether the given ordered pair is an enclave relationship.
   * Returns 'inside' when `from` is contained inside `to`, 'surrounds' when
   * `from` contains `to`, or null when there's no enclave relationship.
   */
  getEnclaveRelation(from: string, to: string): 'inside' | 'surrounds' | null {
    for (const [inner, outer] of this.enclavePairs) {
      if (inner === from && outer === to) return 'inside'
      if (inner === to && outer === from) return 'surrounds'
    }
    return null
  }

  getCentroid(countryName: string): Centroid | null {
    const center = this.centroids.find(center => center.country === countryName)
    if (!center) {
      console.log(`DataService: No centroid found for '${countryName}'`)
      // Show some sample country names for comparison
      const sampleNames = this.centroids
        .slice(0, 10)
        .map(c => c.country)
      console.log('DataService: Sample centroid names:', sampleNames)
    }
    return center || null
  }

  findDistanceBetweenCountries(country1: string, country2: string): CountryDistance | null {
    if (!this.isLoaded) {
      console.warn('DataService: Data not loaded yet')
      return null
    }
    
    if (this.useOptimizedFormat) {
      const optimizedResult = findOptimizedDistanceBetweenCountries(this.optimizedPairs, country1, country2)
      if (optimizedResult) {
        // Get centroids for both countries
        const fromCenter = this.getCentroid(country1)
        const toCenter = this.getCentroid(country2)
        
        // Extract border points from optimized data
        const [point1, point2] = optimizedResult.borderPoints
        
        // Convert optimized format back to legacy CountryDistance format
        return {
          fromCountry: country1,
          toCountry: country2,
          distanceKm: optimizedResult.distance,
          cardinalDirection: optimizedResult.direction,
          azimuthDegrees: optimizedResult.bearing,
          fromPointLon: point1[0], // longitude of first border point
          fromPointLat: point1[1], // latitude of first border point
          toPointLon: point2[0],   // longitude of second border point
          toPointLat: point2[1],   // latitude of second border point
          fromCenterLon: fromCenter?.longitude ?? 0,
          fromCenterLat: fromCenter?.latitude ?? 0,
          toCenterLon: toCenter?.longitude ?? 0,
          toCenterLat: toCenter?.latitude ?? 0,
          rhumbLineBearings: optimizedResult.rhumbLineBearings,
          rhumbLineBearing: optimizedResult.rhumbLineBearing
        }
      }
    } else {
      const result = this.countryDistances.find(
        (d) =>
          (d.fromCountry === country1 && d.toCountry === country2) ||
          (d.fromCountry === country2 && d.toCountry === country1)
      )
      
      if (result) {
        return result
      }
    }
    
    console.log(`DataService: No distance found between ${country1} and ${country2}`)
    return null
  }

  getCountryByName(name: string): any {
    console.log(`DataService: Looking up country by name '${name}'`)
    
    if (!this.countryData) {
      console.log('DataService: No country data loaded')
      return null
    }
    
    const result = this.countryData.features.find(
      (feature) => feature.properties?.NAME === name
    )
    
    if (result) {
      console.log(`DataService: Found country: ${result.properties?.NAME}`)
    } else {
      console.log(`DataService: No country found for name '${name}'`)
      
      // Show some sample country names for comparison
      const sampleNames = this.countryData.features
        .slice(0, 10)
        .map(f => f.properties?.NAME)
        .filter(Boolean)
      console.log('DataService: Sample country names in country data:', sampleNames)
    }
    
    return result
  }
}